import { useState } from 'react';
import { useSites } from '../hooks/useSites';
import { useUpdateModuleData, useModuleData } from '../hooks/useModuleData';
import type { BusinessInfo } from '@inboundhost/shared';

export function SiteSettingsPage() {
  const { data } = useSites();
  const site = data?.sites[0];

  const { data: bi } = useModuleData<BusinessInfo>(site?.id, 'business-info');
  const update = useUpdateModuleData(site?.id ?? '', 'business-info');

  const [form, setForm] = useState<BusinessInfo | null>(null);
  const defaultBusinessInfo: BusinessInfo | null = site ? { name: site.name } : null;
  const value = form ?? bi?.data ?? defaultBusinessInfo;

  if (!site || !value) {
    return <div className="p-8 text-gray-500">Loading…</div>;
  }

  return (
    <div className="p-8 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Site settings</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(value);
        }}
        className="bg-white rounded shadow p-6 space-y-4"
      >
        <Field label="Business name">
          <input
            className="w-full border rounded px-3 py-2"
            value={value.name}
            onChange={(e) => setForm({ ...value, name: e.target.value })}
          />
        </Field>

        <Field label="Description">
          <textarea
            className="w-full border rounded px-3 py-2"
            rows={3}
            value={value.description ?? ''}
            onChange={(e) => setForm({ ...value, description: e.target.value })}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <input
              className="w-full border rounded px-3 py-2"
              value={value.phone ?? ''}
              onChange={(e) => setForm({ ...value, phone: e.target.value })}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              className="w-full border rounded px-3 py-2"
              value={value.email ?? ''}
              onChange={(e) => setForm({ ...value, email: e.target.value })}
            />
          </Field>
        </div>

        <fieldset className="border-t pt-4">
          <legend className="text-sm font-medium mb-2">Address</legend>
          <Field label="Street">
            <input
              className="w-full border rounded px-3 py-2"
              value={value.address?.street ?? ''}
              onChange={(e) =>
                setForm({
                  ...value,
                  address: {
                    ...(value.address ?? { street: '', city: '', state: '', zip: '' }),
                    street: e.target.value,
                  },
                })
              }
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="City">
              <input
                className="w-full border rounded px-3 py-2"
                value={value.address?.city ?? ''}
                onChange={(e) =>
                  setForm({
                    ...value,
                    address: {
                      ...(value.address ?? { street: '', city: '', state: '', zip: '' }),
                      city: e.target.value,
                    },
                  })
                }
              />
            </Field>
            <Field label="State">
              <input
                className="w-full border rounded px-3 py-2"
                value={value.address?.state ?? ''}
                onChange={(e) =>
                  setForm({
                    ...value,
                    address: {
                      ...(value.address ?? { street: '', city: '', state: '', zip: '' }),
                      state: e.target.value,
                    },
                  })
                }
              />
            </Field>
            <Field label="ZIP">
              <input
                className="w-full border rounded px-3 py-2"
                value={value.address?.zip ?? ''}
                onChange={(e) =>
                  setForm({
                    ...value,
                    address: {
                      ...(value.address ?? { street: '', city: '', state: '', zip: '' }),
                      zip: e.target.value,
                    },
                  })
                }
              />
            </Field>
          </div>
        </fieldset>

        <button
          type="submit"
          disabled={update.isPending}
          className="bg-gray-900 text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>

        {update.isSuccess && <span className="ml-3 text-sm text-green-600">Saved</span>}
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-1">{label}</span>
      {children}
    </label>
  );
}
